import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { trackEvent } from "@/lib/analytics";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { CloudflareVideoPlayer } from "@/components/cloudflare-video-player";
import { SharedVideoGuideModal } from "@/components/shared-video-guide-modal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { VideoTimeline } from "@/components/video-timeline";
import { Switch } from "@/components/ui/switch";
import { useSharedVideoGuide } from "@/hooks/use-shared-video-guide";
import { useAuth } from "@/hooks/use-auth";
import {
  FlipHorizontal,
  AlertCircle,
  Loader2,
  Play,
  Pause,
  Repeat,
  Repeat1,
  X,
  ChevronLeft,
  ChevronRight,
  Maximize,
  Minimize,
} from "lucide-react";

const SPEED_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];
const STREAM_PROCESSING_POLL_MS = 3000;
const STREAM_PROCESSING_TIMEOUT_MS = 5 * 60 * 1000;

function clampPan(px: number, py: number, zoom: number, containerW: number, containerH: number) {
  if (zoom <= 1) return { x: 0, y: 0 };
  const maxX = (containerW * (zoom - 1)) / 2;
  const maxY = (containerH * (zoom - 1)) / 2;
  return { x: Math.max(-maxX, Math.min(maxX, px)), y: Math.max(-maxY, Math.min(maxY, py)) };
}

function formatTime(seconds: number) {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function SharedVideoPage() {
  const { token } = useParams<{ token: string }>();
  const auth = useAuth();
  const [processingTimedOut, setProcessingTimedOut] = useState(false);
  const practiceStartedRef = useRef(false);

  const [isMirrored, setIsMirrored] = useState(true);
  const [showLeftRight, setShowLeftRight] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [loopPointA, setLoopPointA] = useState<number | null>(null);
  const [loopPointB, setLoopPointB] = useState<number | null>(null);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [videoLoop, setVideoLoop] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenControls, setShowFullscreenControls] = useState(true);
  const [seekIndicator, setSeekIndicator] = useState<{ amount: number; id: number; side?: 'left' | 'right' } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [showZoomBadge, setShowZoomBadge] = useState(false);
  const zoomBadgeTimerRef = useRef<number | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoLoopRef = useRef(false);
  const videoAreaRef = useRef<HTMLDivElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const controlsTimeoutRef = useRef<number | null>(null);
  const isFullscreenRef = useRef(false);
  const isDraggingRef = useRef(false);
  const isPanningRef = useRef(false);
  const isMirroredRef = useRef(true);
  const dragStartXRef = useRef(0);
  const dragStartYRef = useRef(0);
  const panAtDragStartXRef = useRef(0);
  const panAtDragStartYRef = useRef(0);
  const zoomLevelRef = useRef(1);
  const panXRef = useRef(0);
  const panYRef = useRef(0);
  const isPinchingRef = useRef(false);
  const pinchStartDistRef = useRef(0);
  const pinchStartZoomRef = useRef(1);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const compensateRef = useRef<() => void>(() => {});
  const lastTapTimeRef = useRef<number>(0);
  const singleTapTimerRef = useRef<number | null>(null);
  const [videoAreaEl, setVideoAreaEl] = useState<HTMLDivElement | null>(null);
  const videoAreaCallbackRef = useCallback((el: HTMLDivElement | null) => {
    videoAreaRef.current = el;
    setVideoAreaEl(el);
  }, []);
  const [videoContentRect, setVideoContentRect] = useState<{
    top: number; left: number; right: number; bottom: number;
  } | null>(null);
  const [containerDims, setContainerDims] = useState<{ w: number; h: number } | null>(null);

  const updateVideoContentRect = useCallback(() => {
    const container = videoAreaRef.current;
    if (!container) { setVideoContentRect(null); return; }
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const videoEl = videoRef.current;
    if (videoEl && videoEl.videoWidth && videoEl.videoHeight) {
      const videoAspect = videoEl.videoWidth / videoEl.videoHeight;
      const containerAspect = containerW / containerH;
      let renderW: number, renderH: number;
      if (videoAspect > containerAspect) {
        renderW = containerW;
        renderH = containerW / videoAspect;
      } else {
        renderH = containerH;
        renderW = containerH * videoAspect;
      }
      const top = (containerH - renderH) / 2;
      const left = (containerW - renderW) / 2;
      setContainerDims({ w: containerW, h: containerH });
      setVideoContentRect({ top, left, right: left, bottom: top });
    } else {
      setContainerDims(containerW && containerH ? { w: containerW, h: containerH } : null);
      setVideoContentRect(null);
    }
  }, []);

  const visibleVideoRect = useMemo(() => {
    if (!videoContentRect || !containerDims) return null;
    const { w: cW, h: cH } = containerDims;
    const cx = cW / 2, cy = cH / 2;
    const vcLeft = videoContentRect.left;
    const vcTop = videoContentRect.top;
    const vcRight = cW - videoContentRect.right;
    const vcBottom = cH - videoContentRect.bottom;
    const visLeft   = Math.max(0,  cx + zoomLevel * (vcLeft   - cx) + panX);
    const visTop    = Math.max(0,  cy + zoomLevel * (vcTop    - cy) + panY);
    const visRight  = Math.min(cW, cx + zoomLevel * (vcRight  - cx) + panX);
    const visBottom = Math.min(cH, cy + zoomLevel * (vcBottom - cy) + panY);
    return {
      top:     visTop,
      left:    visLeft,
      right:   cW - visRight,
      bottom:  cH - visBottom,
      centerX: (visLeft + visRight) / 2,
    };
  }, [videoContentRect, containerDims, zoomLevel, panX, panY]);

  useEffect(() => {
    const container = videoAreaRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => updateVideoContentRect());
    observer.observe(container);
    const videoEl = videoRef.current;
    if (videoEl) {
      videoEl.addEventListener("loadedmetadata", updateVideoContentRect);
      videoEl.addEventListener("resize", updateVideoContentRect);
    }
    return () => {
      observer.disconnect();
      if (videoEl) {
        videoEl.removeEventListener("loadedmetadata", updateVideoContentRect);
        videoEl.removeEventListener("resize", updateVideoContentRect);
      }
    };
  }, [videoAreaEl, updateVideoContentRect]);

  const { data: video, isLoading, isError, error } = useQuery<{
    id: number;
    title: string | null;
    thumbnailUrl: string | null;
    videoType: string;
    cloudflareStreamId: string | null;
    durationSeconds: number | null;
    canUseAbLoop: boolean;
    canUseVideoLoop: boolean;
  }>({
    queryKey: ["/api/shared", token],
    queryFn: async () => {
      const res = await fetch(`/api/shared/${token}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw Object.assign(new Error(err.message ?? "Not found"), { status: res.status });
      }
      return res.json();
    },
    retry: false,
    staleTime: Infinity,
    enabled: !!token,
  });

  const {
    data: streamData,
    isError: isStreamError,
    error: streamError,
    refetch: refetchStream,
  } = useQuery<{
    type: string;
    status?: "ready" | "processing";
    progress?: number;
    hlsUrl?: string;
    url?: string;
  }>({
    queryKey: ["/api/shared", token, "stream"],
    queryFn: async () => {
      const res = await fetch(`/api/shared/${token}/stream`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw Object.assign(new Error(err.message ?? "Failed to load stream"), { status: res.status });
      }
      return res.json();
    },
    enabled: !!video,
    retry: false,
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data?.status === "processing" && !processingTimedOut
        ? STREAM_PROCESSING_POLL_MS
        : false,
  });

  const sharedVideoGuide = useSharedVideoGuide(
    !!video && !auth.isLoading && !auth.user
  );

  useEffect(() => {
    setProcessingTimedOut(false);
  }, [token]);

  useEffect(() => {
    if (streamData?.status !== "processing" || processingTimedOut) return;
    const timeoutId = window.setTimeout(
      () => setProcessingTimedOut(true),
      STREAM_PROCESSING_TIMEOUT_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [streamData?.status, processingTimedOut]);

  useEffect(() => {
    document.title = video?.title
      ? `${video.title} | DanceMirror`
      : "Shared Video | DanceMirror";
    return () => { document.title = "DanceMirror"; };
  }, [video?.title]);

  const hlsUrl = streamData?.hlsUrl ?? streamData?.url ?? "";
  const is404 = isError && (error as any)?.status === 404;
  const isStream404 = isStreamError && (streamError as any)?.status === 404;
  const streamErrorMessage = streamError instanceof Error
    ? streamError.message
    : "Could not load this video. Please try again.";
  const isPaid = auth.user &&
    ["monthly", "yearly", "lifetime"].includes(auth.user.subscriptionTier ?? "");

  // Auto-hide controls timer (fullscreen only)
  const startHideTimer = useCallback(() => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = window.setTimeout(() => {
      setShowFullscreenControls(false);
    }, 3000);
  }, []);

  // Reset inactivity timer when user interacts with controls in fullscreen
  const resetHideTimer = useCallback(() => {
    if (isFullscreenRef.current) startHideTimer();
  }, [startHideTimer]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); } else { v.pause(); }
    resetHideTimer();
  }, [resetHideTimer]);

  const skipTime = useCallback((seconds: number, side?: 'left' | 'right') => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + seconds));
    setSeekIndicator({ amount: seconds, id: Date.now(), side });
    resetHideTimer();
  }, [resetHideTimer]);

  const handleSeek = useCallback((value: number[]) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = value[0];
    setCurrentTime(value[0]);
  }, []);

  const changeSpeed = useCallback((speed: number) => {
    setPlaybackSpeed(speed);
    const v = videoRef.current;
    if (v) v.playbackRate = speed;
    trackEvent("practice_control_used", {
      surface: "shared",
      control: "speed",
      value: speed,
    });
    resetHideTimer();
  }, [resetHideTimer]);

  const setPointA = useCallback(() => {
    if (!video?.canUseAbLoop) return;
    setLoopPointA(currentTime);
    if (loopPointB !== null && currentTime >= loopPointB) {
      setLoopPointB(null);
      setLoopEnabled(false);
    }
  }, [currentTime, loopPointB, video?.canUseAbLoop]);

  const setPointB = useCallback(() => {
    if (!video?.canUseAbLoop || loopPointA === null || currentTime <= loopPointA) return;
    setLoopPointB(currentTime);
    setLoopEnabled(true);
    setVideoLoop(false);
    videoLoopRef.current = false;
    trackEvent("practice_control_used", {
      surface: "shared",
      control: "ab_loop",
      enabled: true,
    });
  }, [currentTime, loopPointA, video?.canUseAbLoop]);

  const clearLoop = useCallback(() => {
    if (!video?.canUseAbLoop) return;
    setLoopPointA(null);
    setLoopPointB(null);
    setLoopEnabled(false);
  }, [video?.canUseAbLoop]);

  const toggleVideoLoop = useCallback((enabled: boolean) => {
    if (!video?.canUseVideoLoop) return;
    setVideoLoop(enabled);
    videoLoopRef.current = enabled;
    trackEvent("practice_control_used", {
      surface: "shared",
      control: "video_loop",
      enabled,
    });
    if (enabled) {
      setLoopEnabled(false);
    }
    resetHideTimer();
  }, [resetHideTimer, video?.canUseVideoLoop]);

  useEffect(() => {
    if (!video?.canUseAbLoop || !loopEnabled || loopPointA === null || loopPointB === null) return;
    if (currentTime >= loopPointB && videoRef.current) {
      videoRef.current.currentTime = loopPointA;
    }
  }, [currentTime, loopEnabled, loopPointA, loopPointB, video?.canUseAbLoop]);

  useEffect(() => {
    setLoopPointA(null);
    setLoopPointB(null);
    setLoopEnabled(false);
    setVideoLoop(false);
    videoLoopRef.current = false;
    practiceStartedRef.current = false;
  }, [token]);

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true);
    if (practiceStartedRef.current) return;
    practiceStartedRef.current = true;
    trackEvent("practice_started", {
      surface: "shared",
      source_type: "upload",
    });
  }, []);

  useEffect(() => {
    if (!video?.canUseVideoLoop) {
      setVideoLoop(false);
      videoLoopRef.current = false;
    }
  }, [video?.canUseVideoLoop]);

  const handleVideoEnded = useCallback(() => {
    if (!video?.canUseVideoLoop || !videoLoopRef.current) {
      setIsPlaying(false);
      return;
    }

    const videoElement = videoRef.current;
    if (!videoElement) {
      setIsPlaying(false);
      return;
    }

    videoElement.currentTime = 0;
    setCurrentTime(0);
    videoElement.play().catch(() => setIsPlaying(false));
  }, [video?.canUseVideoLoop]);

  const toggleFullscreen = useCallback(() => {
    trackEvent("practice_control_used", {
      surface: "shared",
      control: "fullscreen",
      enabled: !isFullscreenRef.current,
    });
    if (!isFullscreenRef.current) {
      const el = videoContainerRef.current as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void>;
      };
      if (el?.requestFullscreen) {
        el.requestFullscreen().catch(() => {});
      } else if (el?.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      const doc = document as Document & {
        webkitExitFullscreen?: () => void;
        webkitFullscreenElement?: Element | null;
      };
      if (doc.fullscreenElement || doc.webkitFullscreenElement) {
        if (doc.exitFullscreen) doc.exitFullscreen().catch(() => {});
        else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
      }
      setIsFullscreen(false);
    }
  }, []);

  const handleVideoAreaTap = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
      lastTapTimeRef.current = 0;
      return;
    }

    const now = Date.now();
    const isDoubleTap = now - lastTapTimeRef.current < 300;

    if (isDoubleTap) {
      if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
      lastTapTimeRef.current = 0;
      const rect = e.currentTarget.getBoundingClientRect();
      const isRightHalf = e.clientX - rect.left > rect.width / 2;
      skipTime(isRightHalf ? 5 : -5, isRightHalf ? 'right' : 'left');
      if (isFullscreenRef.current) {
        setShowFullscreenControls(true);
        startHideTimer();
      }
    } else {
      lastTapTimeRef.current = now;
      singleTapTimerRef.current = window.setTimeout(() => {
        if (isFullscreenRef.current) {
          togglePlay();
          setShowFullscreenControls(true);
          startHideTimer();
        } else {
          togglePlay();
        }
      }, 300);
    }
  }, [skipTime, startHideTimer, togglePlay]);

  // Keep ref in sync for callbacks that can't take isFullscreen as a dependency
  useEffect(() => { isFullscreenRef.current = isFullscreen; }, [isFullscreen]);
  useEffect(() => { isMirroredRef.current = isMirrored; }, [isMirrored]);
  useEffect(() => { zoomLevelRef.current = zoomLevel; }, [zoomLevel]);
  useEffect(() => { panXRef.current = panX; }, [panX]);
  useEffect(() => { panYRef.current = panY; }, [panY]);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const next = Math.min(4, prev + 0.5);
      const container = videoAreaRef.current;
      if (container) {
        const c = clampPan(panXRef.current, panYRef.current, next, container.clientWidth, container.clientHeight);
        setPanX(c.x); setPanY(c.y);
      }
      return next;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const next = Math.max(1, prev - 0.5);
      if (next <= 1) { setPanX(0); setPanY(0); return 1; }
      const container = videoAreaRef.current;
      if (container) {
        const c = clampPan(panXRef.current, panYRef.current, next, container.clientWidth, container.clientHeight);
        setPanX(c.x); setPanY(c.y);
      }
      return next;
    });
  }, []);

  const handleVideoPanStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    if (isPinchingRef.current) return;
    isDraggingRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartYRef.current = e.clientY;
    panAtDragStartXRef.current = panXRef.current;
    panAtDragStartYRef.current = panYRef.current;
    if (zoomLevelRef.current > 1) {
      isPanningRef.current = true;
      try { (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId); } catch {}
      setIsGrabbing(true);
    }
  }, []);

  const handleVideoPanMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPanningRef.current) return;
    if (isPinchingRef.current) return;
    if (zoomLevelRef.current <= 1) return;
    const dx = e.clientX - dragStartXRef.current;
    const dy = e.clientY - dragStartYRef.current;
    if (!isDraggingRef.current && Math.sqrt(dx * dx + dy * dy) < 6) return;
    isDraggingRef.current = true;
    const container = videoAreaRef.current;
    if (!container) return;
    // Negate dx when mirrored: scaleX(-1) flips the local X axis so positive
    // screen-delta maps to the wrong direction without the correction.
    const panDx = isMirroredRef.current ? -dx : dx;
    const c = clampPan(
      panAtDragStartXRef.current + panDx / zoomLevelRef.current,
      panAtDragStartYRef.current + dy / zoomLevelRef.current,
      zoomLevelRef.current, container.clientWidth, container.clientHeight
    );
    setPanX(c.x); setPanY(c.y);
  }, []);

  const handleVideoPanEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    isPanningRef.current = false;
    try { (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId); } catch {}
    setIsGrabbing(false);
  }, []);

  const handleVideoPanCancel = useCallback(() => {
    isPanningRef.current = false;
    isDraggingRef.current = false;
    setIsGrabbing(false);
  }, []);

  // Scroll-wheel / trackpad-pinch zoom
  useEffect(() => {
    const el = videoAreaRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (e.deltaY < 0) zoomIn(); else zoomOut();
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [videoAreaEl, zoomIn, zoomOut]);

  // Native touch handlers: pinch-to-zoom + prevent browser viewport zoom
  useEffect(() => {
    const el = videoAreaRef.current;
    if (!el) return;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
        // If the browser has already applied viewport zoom, skip JS pinch setup
        // to avoid double-zooming the video content.
        const vvCheck = window.visualViewport;
        if (vvCheck && vvCheck.scale > 1.01) return;
        if (!isPinchingRef.current) {
          isPinchingRef.current = true;
          isPanningRef.current = false;
          isDraggingRef.current = false;
          setIsGrabbing(false);
          if (zoomBadgeTimerRef.current) clearTimeout(zoomBadgeTimerRef.current);
          setShowZoomBadge(true);
          const dx = e.touches[0].clientX - e.touches[1].clientX;
          const dy = e.touches[0].clientY - e.touches[1].clientY;
          pinchStartDistRef.current = Math.sqrt(dx * dx + dy * dy);
          pinchStartZoomRef.current = zoomLevelRef.current;
        }
      } else {
        isPinchingRef.current = false;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        e.preventDefault();
        if (!isPinchingRef.current) return;
        // If the browser has taken over with viewport zoom, don't stack JS zoom on top.
        const vvCheck = window.visualViewport;
        if (vvCheck && vvCheck.scale > 1.01) return;
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const scale = dist / pinchStartDistRef.current;
        const newZoom = Math.max(1, Math.min(4, pinchStartZoomRef.current * scale));
        setZoomLevel(newZoom);
        const container = videoAreaRef.current;
        if (container) {
          const c = clampPan(panXRef.current, panYRef.current, newZoom, container.clientWidth, container.clientHeight);
          setPanX(c.x);
          setPanY(c.y);
        }
      } else if (isPanningRef.current) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) {
        isPinchingRef.current = false;
        if (zoomBadgeTimerRef.current) clearTimeout(zoomBadgeTimerRef.current);
        zoomBadgeTimerRef.current = window.setTimeout(() => setShowZoomBadge(false), 1200);
      }
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    el.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [videoAreaEl]);

  // Block iOS Safari viewport zoom for the lifetime of this page.
  // iOS commits to its native pinch-zoom gesture on touchstart (not touchmove),
  // so we must intercept it there with a non-passive listener. The touchmove
  // listener is kept as a second line of defence. Both are scoped to this page
  // via cleanup on unmount.
  useEffect(() => {
    const blockPinch = (e: TouchEvent) => {
      if (e.touches.length >= 2) e.preventDefault();
    };
    // capture: true fires before any bubble-phase stopPropagation can silence it,
    // ensuring pinch-zoom is blocked even when the user's fingers land on the controls.
    document.addEventListener("touchstart", blockPinch, { passive: false, capture: true });
    document.addEventListener("touchmove", blockPinch, { passive: false, capture: true });
    return () => {
      document.removeEventListener("touchstart", blockPinch, { capture: true });
      document.removeEventListener("touchmove", blockPinch, { capture: true });
    };
  }, []);

  // Dynamic viewport meta: add user-scalable=no on mount so iOS 16+ respects it.
  // Preserves all existing directives (including viewport-fit=cover for notch
  // devices) by parsing and merging rather than replacing the whole string.
  // Restores original content on unmount so other pages are unaffected.
  useEffect(() => {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    if (!metaViewport) return;
    const original = metaViewport.getAttribute("content") || "";
    const directives: Record<string, string> = {};
    original.split(",").forEach((part) => {
      const [k, v] = part.trim().split("=");
      if (k) directives[k.trim()] = (v ?? "").trim();
    });
    directives["user-scalable"] = "no";
    directives["minimum-scale"] = "1";
    directives["maximum-scale"] = "1";
    metaViewport.setAttribute(
      "content",
      Object.entries(directives)
        .map(([k, v]) => (v ? `${k}=${v}` : k))
        .join(", ")
    );
    return () => {
      metaViewport.setAttribute("content", original);
    };
  }, []);

  // Visual Viewport API compensation: when the browser viewport is zoomed (e.g.
  // by a pinch gesture that WKWebView processes before JS handlers), apply an
  // inverse CSS scale to the controls container so it always appears at 1× visual
  // size. Falls back gracefully on browsers without visualViewport support.
  // compensate() is called immediately after listener registration so the
  // transform is correct if the page is entered while already zoomed.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const compensate = () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const s = vv.scale;
      if (s <= 1.001 || !isFullscreenRef.current) {
        // Reset all inline overrides — Tailwind classes take over.
        controls.style.transform = "";
        controls.style.transformOrigin = "";
        controls.style.width = "";
        controls.style.left = "";
        controls.style.right = "";
        controls.style.bottom = "";
        return;
      }
      // Place controls at the BOTTOM of the visual viewport, full-width, 1× visual size.
      //
      // vv.offsetLeft/Top — left/top edge of visual viewport in layout CSS px
      // vv.width/height   — visual viewport size in layout CSS px
      // window.innerWidth — full layout viewport width in layout CSS px
      //
      // Width before scale: window.innerWidth layout px → after scale(1/s) → window.innerWidth/s
      //   = vv.width layout px = exactly the visible width ✓
      //
      // bottomGap = layout px between visual-viewport-bottom and layout-viewport-bottom.
      // controls.style.bottom = bottomGap keeps the bottom of the controls flush with
      // the bottom of the visual viewport.
      const bottomGap = window.innerHeight - vv.offsetTop - vv.height;
      controls.style.left = `${vv.offsetLeft}px`;
      controls.style.right = "auto";
      controls.style.width = `${window.innerWidth}px`;
      controls.style.bottom = `${bottomGap}px`;
      controls.style.transformOrigin = "bottom left";
      controls.style.transform = `scale(${1 / s})`;
    };
    compensateRef.current = compensate;
    vv.addEventListener("resize", compensate);
    vv.addEventListener("scroll", compensate);
    compensate();
    return () => {
      vv.removeEventListener("resize", compensate);
      vv.removeEventListener("scroll", compensate);
    };
  }, []);

  // Show controls + measure viewport when entering/leaving fullscreen
  useEffect(() => {
    let vpTimer: number;
    let rectTimer: number;
    if (isFullscreen) {
      setShowFullscreenControls(true);
      startHideTimer();
      vpTimer = window.setTimeout(() => {
        const vv = window.visualViewport;
        if (vv) {
          document.documentElement.style.setProperty("--real-vh", `${vv.height * 0.01}px`);
          document.documentElement.style.setProperty("--vv-top", `${vv.offsetTop}px`);
        } else {
          document.documentElement.style.setProperty("--real-vh", `${window.innerHeight * 0.01}px`);
          document.documentElement.style.setProperty("--vv-top", "0px");
        }
      }, 50);
    } else {
      setShowFullscreenControls(true);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    }
    // Re-run compensation so controls are correctly positioned if the browser is
    // already zoomed when fullscreen is entered or exited.
    compensateRef.current();
    rectTimer = window.setTimeout(updateVideoContentRect, 100);
    return () => {
      clearTimeout(vpTimer);
      clearTimeout(rectTimer);
    };
  }, [isFullscreen, startHideTimer, updateVideoContentRect]);

  // Sync state when browser ESC exits fullscreen natively
  useEffect(() => {
    const handleFsChange = () => {
      const doc = document as Document & { webkitFullscreenElement?: Element | null };
      if (!doc.fullscreenElement && !doc.webkitFullscreenElement) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
    };
  }, []);

  // Keyboard shortcuts (fullscreen only)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isFullscreenRef.current) return;
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        togglePlay();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        skipTime(-5);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        skipTime(5);
      } else if (e.key === "Escape") {
        const doc = document as Document & {
          webkitExitFullscreen?: () => void;
          webkitFullscreenElement?: Element | null;
        };
        if (doc.fullscreenElement || doc.webkitFullscreenElement) {
          if (doc.exitFullscreen) doc.exitFullscreen().catch(() => {});
          else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
        }
        setIsFullscreen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [togglePlay, skipTime]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      if (zoomBadgeTimerRef.current) clearTimeout(zoomBadgeTimerRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SharedVideoGuideModal
        open={sharedVideoGuide.open}
        onDismiss={sharedVideoGuide.dismiss}
        canUseAbLoop={video?.canUseAbLoop ?? false}
      />
      <AppHeader />

      <main className="flex-1 container mx-auto px-4 py-6 max-w-4xl">
        {isLoading && (
          <div className="space-y-4">
            <div className="aspect-video bg-muted rounded-lg animate-pulse" />
            <div className="h-6 bg-muted rounded w-1/2 animate-pulse" />
          </div>
        )}

        {(is404 || isStream404) && (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
            <AlertCircle className="w-12 h-12 text-muted-foreground" />
            <h1 className="text-xl font-semibold">Video unavailable</h1>
            <p className="text-muted-foreground max-w-sm">
              This link is no longer active or has been disabled by the owner.
            </p>
            <Link href="/"><Button variant="outline">Go to DanceMirror</Button></Link>
          </div>
        )}

        {isError && !is404 && (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
            <AlertCircle className="w-12 h-12 text-muted-foreground" />
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground">Could not load this video. Please try again later.</p>
          </div>
        )}

        {isStreamError && !isStream404 && (
          <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
            <AlertCircle className="w-12 h-12 text-muted-foreground" />
            <h1 className="text-xl font-semibold">Video unavailable</h1>
            <p className="text-muted-foreground max-w-sm">{streamErrorMessage}</p>
            <Button variant="outline" onClick={() => refetchStream()}>
              Try again
            </Button>
          </div>
        )}

        {video && !isStreamError && (
          <div className="space-y-4">
            <div
              ref={videoContainerRef}
              className={isFullscreen ? "fixed left-0 right-0 z-[100] bg-black flex flex-col safe-area-inset" : ""}
              style={isFullscreen ? { top: "var(--vv-top, 0px)", height: "calc(var(--real-vh, 1vh) * 100)" } : undefined}
            >
              <Card className={`overflow-hidden${isFullscreen ? " flex-1 flex flex-col rounded-none border-0" : ""}`}>
                {/* Video area */}
                <div
                  ref={videoAreaCallbackRef}
                  className={`relative bg-black overflow-hidden${isFullscreen ? " flex-1" : " aspect-video"}`}
                  style={{
                    transform: isMirrored ? "scaleX(-1)" : "none",
                    transition: "transform 0.3s ease",
                    cursor: zoomLevel > 1 ? (isGrabbing ? "grabbing" : "grab") : "pointer",
                    touchAction: "none",
                  }}
                  onClick={handleVideoAreaTap}
                  onPointerDown={handleVideoPanStart}
                  onPointerMove={handleVideoPanMove}
                  onPointerUp={handleVideoPanEnd}
                  onPointerCancel={handleVideoPanCancel}
                  data-testid="video-area"
                >
                  {/* Zoom wrapper — wraps only the video, not the overlays */}
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                      transformOrigin: "center center",
                      willChange: zoomLevel > 1 ? "transform" : "auto",
                    }}
                  >
                    {hlsUrl ? (
                      <CloudflareVideoPlayer
                        hlsUrl={hlsUrl}
                        playbackSpeed={playbackSpeed}
                        onVideoRef={(el) => {
                          if (videoRef.current) {
                            videoRef.current.removeEventListener("loadedmetadata", updateVideoContentRect);
                            videoRef.current.removeEventListener("resize", updateVideoContentRect);
                            videoRef.current.removeEventListener("loadeddata", updateVideoContentRect);
                          }
                          videoRef.current = el;
                          if (el) {
                            el.addEventListener("loadedmetadata", updateVideoContentRect);
                            el.addEventListener("loadeddata", updateVideoContentRect);
                            el.addEventListener("resize", updateVideoContentRect);
                          }
                        }}
                        onTimeUpdate={setCurrentTime}
                        onDurationChange={(dur) => { setDuration(dur); updateVideoContentRect(); }}
                        onPlay={handleVideoPlay}
                        onPause={() => setIsPlaying(false)}
                        onEnded={handleVideoEnded}
                        className="w-full h-full object-contain pointer-events-none"
                      />
                    ) : streamData?.status === "processing" && !processingTimedOut ? (
                      <div
                        className="flex flex-col items-center justify-center gap-2 w-full h-full text-muted-foreground text-sm px-6 text-center"
                        style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}
                      >
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Video is being prepared. This page will update automatically.</span>
                        {typeof streamData.progress === "number" && streamData.progress > 0 && (
                          <span className="text-xs">{Math.round(streamData.progress)}% complete</span>
                        )}
                      </div>
                    ) : streamData?.status === "processing" ? (
                      <div
                        className="flex flex-col items-center justify-center gap-3 w-full h-full text-muted-foreground text-sm px-6 text-center"
                        style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}
                      >
                        <AlertCircle className="w-5 h-5" />
                        <span>This video is taking longer than expected to prepare.</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            setProcessingTimedOut(false);
                            refetchStream();
                          }}
                        >
                          Check again
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="flex items-center justify-center w-full h-full text-muted-foreground text-sm"
                        style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}
                      >
                        Loading video...
                      </div>
                    )}

                  </div>

                  {/* MIRRORED badge — outside zoom wrapper, tracks visible video bounds */}
                  {isMirrored && (
                    <div
                      className="absolute z-10 pointer-events-none"
                      style={{
                        top: visibleVideoRect ? `${visibleVideoRect.top + 8}px` : "8px",
                        left: visibleVideoRect ? `${visibleVideoRect.centerX}px` : "50%",
                        transform: "translateX(-50%) scaleX(-1)",
                      }}
                      data-testid="badge-mirrored"
                    >
                      <div className="flex items-center gap-1 bg-primary/80 text-primary-foreground text-[10px] sm:text-xs font-semibold px-2 py-0.5 rounded-full shadow-md">
                        <FlipHorizontal className="w-3 h-3" />
                        MIRRORED
                      </div>
                    </div>
                  )}

                  {/* L/R circles overlay — outside zoom wrapper, tracks visible video bounds */}
                  {showLeftRight && (
                    <div
                      className="absolute pointer-events-none flex justify-between items-start p-3 sm:p-4 z-10"
                      style={{
                        transform: isMirrored ? "scaleX(-1)" : "none",
                        top:    visibleVideoRect ? `${visibleVideoRect.top}px`    : 0,
                        left:   visibleVideoRect ? `${visibleVideoRect.left}px`   : 0,
                        right:  visibleVideoRect ? `${visibleVideoRect.right}px`  : 0,
                        bottom: visibleVideoRect ? `${visibleVideoRect.bottom}px` : 0,
                      }}
                    >
                      <div className="bg-white/90 rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center shadow-lg">
                        <span className="text-black font-bold text-base sm:text-lg">{isMirrored ? "L" : "R"}</span>
                      </div>
                      <div className="bg-white/90 rounded-full w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center shadow-lg">
                        <span className="text-black font-bold text-base sm:text-lg">{isMirrored ? "R" : "L"}</span>
                      </div>
                    </div>
                  )}

                  {/* Pinch-zoom level badge — outside zoom wrapper, stays fixed */}
                  <div
                    className="absolute inset-0 flex items-center justify-center pointer-events-none z-30"
                    style={{
                      opacity: showZoomBadge && zoomLevel > 1 ? 1 : 0,
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    <div className="bg-black/60 text-white text-sm font-semibold px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm" style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}>
                      {zoomLevel.toFixed(1)}×
                    </div>
                  </div>

                  {/* Seek indicator flash */}
                  {seekIndicator && (
                    <div
                      className={`absolute inset-0 flex items-center pointer-events-none z-20 ${seekIndicator.side === 'left' ? 'justify-start pl-10' : seekIndicator.side === 'right' ? 'justify-end pr-10' : 'justify-center'}`}
                      style={{ transform: isMirrored ? "scaleX(-1)" : "none" }}
                    >
                      <div
                        key={seekIndicator.id}
                        className="bg-black/60 text-white font-bold text-3xl px-5 py-2 rounded-full animate-seek-indicator"
                        onAnimationEnd={() => setSeekIndicator(null)}
                      >
                        {seekIndicator.amount > 0 ? `+${seekIndicator.amount}s` : `${seekIndicator.amount}s`}
                      </div>
                    </div>
                  )}

                </div>

                {/* Controls bar */}
                <div
                  ref={controlsRef}
                  className={`bg-card transition-opacity duration-300${isFullscreen ? " bg-black/90 absolute bottom-0 left-0 right-0 z-10" : ""}${isFullscreen && !showFullscreenControls ? " opacity-0 pointer-events-none" : ""}${isFullscreen && showFullscreenControls ? " opacity-100 pointer-events-auto" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  {/* Full-width timeline (YouTube-style) */}
                  <VideoTimeline
                    currentTime={currentTime}
                    duration={duration}
                    onSeek={handleSeek}
                    onSeekCommit={() => { if (isFullscreen) startHideTimer(); }}
                    isFullscreen={isFullscreen}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      if (isFullscreen && controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
                    }}
                  />

                  <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 pb-2 sm:pb-4">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => skipTime(-5)}
                      data-testid="button-skip-back"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      size="icon"
                      onClick={togglePlay}
                      data-testid="button-play-pause"
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => skipTime(5)}
                      data-testid="button-skip-forward"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>

                    <span
                      className="text-xs sm:text-sm text-muted-foreground min-w-[60px] sm:min-w-[80px]"
                      data-testid="text-time"
                    >
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>

                    <div className="flex-1" />

                    <select
                      value={playbackSpeed}
                      onChange={(e) => changeSpeed(parseFloat(e.target.value))}
                      className="bg-muted text-foreground text-xs sm:text-sm px-2 py-1 rounded border border-border focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="select-speed"
                    >
                      {SPEED_OPTIONS.map((speed) => (
                        <option key={speed} value={speed}>{speed}x</option>
                      ))}
                    </select>

                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={toggleFullscreen}
                      data-testid="button-fullscreen"
                    >
                      {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                    </Button>
                  </div>

                  <div className="flex items-center justify-between gap-2 px-2 sm:px-4 pb-2 sm:pb-4 border-t border-border/50 flex-wrap pt-2">
                    <div className="flex items-center gap-4 flex-wrap">
                      <label
                        htmlFor="shared-switch-mirror"
                        className="flex items-center gap-1.5 cursor-pointer"
                        data-testid="toggle-mirror"
                      >
                        <FlipHorizontal className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground select-none">
                          {isMirrored ? "Mirrored" : "Original"}
                        </span>
                        <Switch
                          id="shared-switch-mirror"
                          checked={isMirrored}
                          onCheckedChange={(v) => {
                            setIsMirrored(v);
                            trackEvent("practice_control_used", {
                              surface: "shared",
                              control: "mirror",
                              enabled: v,
                            });
                            resetHideTimer();
                          }}
                          aria-label="Toggle mirror mode"
                          data-testid="switch-mirror"
                        />
                      </label>

                      <label
                        htmlFor="shared-switch-lr"
                        className="flex items-center gap-1.5 cursor-pointer"
                        data-testid="toggle-left-right"
                      >
                        <span className="text-xs font-bold text-muted-foreground select-none">L / R</span>
                        <Switch
                          id="shared-switch-lr"
                          checked={showLeftRight}
                          onCheckedChange={(v) => {
                            setShowLeftRight(v);
                            trackEvent("practice_control_used", {
                              surface: "shared",
                              control: "lr_overlay",
                              enabled: v,
                            });
                            resetHideTimer();
                          }}
                          aria-label="Toggle L/R overlay"
                          data-testid="switch-lr"
                        />
                      </label>
                    </div>

                    {(video.canUseVideoLoop || video.canUseAbLoop) && (
                      <div className="flex items-center gap-3 flex-wrap">
                        {video.canUseVideoLoop && (
                          <label
                            htmlFor="shared-switch-video-loop"
                            className={`flex items-center gap-1.5 min-h-[36px] ${
                              loopEnabled && loopPointA !== null && loopPointB !== null
                                ? "opacity-50 pointer-events-none"
                                : "cursor-pointer"
                            }`}
                            data-testid="toggle-video-loop"
                          >
                            <Repeat1 className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground select-none">Loop</span>
                            <Switch
                              id="shared-switch-video-loop"
                              checked={videoLoop}
                              disabled={loopEnabled && loopPointA !== null && loopPointB !== null}
                              onCheckedChange={toggleVideoLoop}
                              aria-label="Toggle video loop"
                              data-testid="shared-switch-video-loop"
                            />
                          </label>
                        )}

                        {video.canUseAbLoop && (
                          <div className="flex items-center gap-1.5 flex-wrap" data-testid="shared-ab-loop-controls">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Repeat className="w-4 h-4" />
                              <span>A-B</span>
                            </div>
                            <Button
                              size="sm"
                              variant={loopPointA !== null ? "default" : "outline"}
                              onClick={setPointA}
                              className="min-h-[36px] px-2"
                              data-testid="shared-button-set-a"
                            >
                              A {loopPointA !== null ? formatTime(loopPointA) : "—"}
                            </Button>
                            <Button
                              size="sm"
                              variant={loopPointB !== null ? "default" : "outline"}
                              onClick={setPointB}
                              disabled={loopPointA === null}
                              className="min-h-[36px] px-2"
                              data-testid="shared-button-set-b"
                            >
                              B {loopPointB !== null ? formatTime(loopPointB) : "—"}
                            </Button>
                            {loopPointA !== null && loopPointB !== null && (
                              <>
                                <label htmlFor="shared-switch-ab-loop" className="flex items-center gap-1.5 cursor-pointer min-h-[36px]">
                                  <span className="text-xs text-muted-foreground">Active</span>
                                  <Switch
                                    id="shared-switch-ab-loop"
                                    checked={loopEnabled}
                                    onCheckedChange={(enabled) => {
                                      setLoopEnabled(enabled);
                                      trackEvent("practice_control_used", {
                                        surface: "shared",
                                        control: "ab_loop",
                                        enabled,
                                      });
                                      if (enabled) {
                                        setVideoLoop(false);
                                        videoLoopRef.current = false;
                                      }
                                    }}
                                    aria-label="Toggle A-B loop"
                                    data-testid="shared-switch-ab-loop"
                                  />
                                </label>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={clearLoop}
                                  className="min-h-[36px] min-w-[36px]"
                                  aria-label="Clear A-B loop"
                                  data-testid="shared-button-clear-loop"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </div>

            {/* Video title — hidden in fullscreen */}
            {!isFullscreen && video.title && (
              <h1 className="text-lg font-semibold" data-testid="text-video-title">{video.title}</h1>
            )}

            {/* CTA banner — hidden in fullscreen */}
            {!isFullscreen && !isPaid && (
              <div className="mt-2 rounded-lg border bg-muted/50 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  {!auth.user ? (
                    <>
                      <p className="font-semibold">Practice smarter with DanceMirror</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {video?.canUseAbLoop
                          ? "Try A-B looping on this shared video. Create a free account to save videos, mirror mode, and more."
                          : "Create a free account to save videos, mirror mode, and more. Upgrade for A-B looping, playlists, and video uploads."}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">Unlock more practice tools</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {video?.canUseAbLoop
                          ? "A-B looping is available on this shared video. Upgrade for video uploads, notes, and playlists."
                          : "Upgrade to access A-B looping, video uploads, notes, and playlists."}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {!auth.user ? (
                    <>
                      <a href={`/auth/google?returnTo=${encodeURIComponent(`/shared/${token}`)}`}>
                        <Button size="sm" data-testid="button-sign-in">Sign in with Google</Button>
                      </a>
                      <Link href="/pricing">
                        <Button variant="outline" size="sm" data-testid="button-view-plans">View Plans</Button>
                      </Link>
                    </>
                  ) : (
                    <Link href="/pricing">
                      <Button size="sm" data-testid="button-view-plans">View Plans</Button>
                    </Link>
                  )}
                </div>
              </div>
            )}

          </div>
        )}
      </main>

      <AppFooter />
    </div>
  );
}
